import React, { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import trim from "lodash/trim";
import split from "lodash/split";
import uniq from "lodash/uniq";
import isEmpty from "lodash/isEmpty";
import isArray from "lodash/isArray";
import last from "lodash/last";
import slice from "lodash/slice";
import LicenseCheck from "license/LicenseCheck";
import UserNotification from "util/UserNotification";
import { Button, ButtonToolbar, Col, Row, Table, Input } from "components/bootstrap";
import { PaginatedList, SearchForm, TimeUnitInput, Timestamp } from "components/common";
import {
  MONGODB_DATA_ADAPTER_QUERY_KEY,
  useMongoDBDataAdapterEntries,
  createMongoDBDataAdapterEntry,
  updateMongoDBDataAdapterEntry,
  deleteMongoDBDataAdapterEntry
} from "../hooks/useMongoDBDataAdapter";
import MongoDBDataAdapterEntry from "../models/MongoDBDataAdapterEntry";
const DEFAULT_TTL = { value: 1, unit: "", enabled: false };
const _errorHandler = (message, title) => (error) => {
  let errorMessage;
  try {
    if (isArray(error.additional.body)) {
      const invalidFields = error.additional.body.map((e) => last(split(e.path, "."))).join(", ");
      errorMessage = `invalid fields: ${invalidFields}`;
    } else {
      errorMessage = error.additional.body.message;
    }
  } catch (_e) {
    errorMessage = error.message;
  }
  UserNotification.error(`${message}: ${errorMessage}`, title);
};
const trimValues = (values) => uniq(values.map((value) => trim(value)).filter((value) => (value != null ? value : "").length > 0));
const MongoDBDataAdapterTable = ({ dataAdapterId }) => {
  var _a;
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [storeId, setStoreId] = useState("");
  const [storeKey, setStoreKey] = useState("");
  const [storeValues, setStoreValues] = useState([]);
  const [ttl, setTtl] = useState(DEFAULT_TTL);
  const [editMode, setEditMode] = useState(false);
  const { data } = useMongoDBDataAdapterEntries({ dataAdapterId, query, page, perPage });
  const pagination = data == null ? void 0 : data.pagination;
  const entries = (_a = data == null ? void 0 : data.entries) != null ? _a : [];
  const reloadEntries = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [...MONGODB_DATA_ADAPTER_QUERY_KEY, "entries"] }),
    [queryClient]
  );
  const clearValues = useCallback(() => {
    setStoreId("");
    setStoreValues([]);
    setStoreKey("");
    setEditMode(false);
    setTtl({ value: 1, unit: "", enabled: false });
  }, []);
  const clearValuesAndReloadEntries = useCallback(() => {
    clearValues();
    reloadEntries();
  }, [clearValues, reloadEntries]);
  const handleSearch = useCallback((newQuery) => {
    setQuery(newQuery);
    setPage(1);
  }, []);
  const handleSearchReset = useCallback(() => {
    setQuery("");
    setPage(1);
  }, []);
  const handlePageChange = useCallback((newPage, newPerPage) => {
    setPage(newPage);
    setPerPage(newPerPage);
  }, []);
  const addEntry = useCallback(
    (e) => {
      var _a2, _b;
      (_a2 = e == null ? void 0 : e.preventDefault) == null ? void 0 : _a2.call(e);
      (_b = e == null ? void 0 : e.stopPropagation) == null ? void 0 : _b.call(e);
      const newEntryBuilder = MongoDBDataAdapterEntry.builder().dataAdapterId(dataAdapterId).id(isEmpty(trim(storeId)) ? null : storeId).key(storeKey).values(trimValues(storeValues)).ttl(ttl.enabled ? ttl.value : null).ttl_unit(ttl.enabled ? ttl.unit : null);
      if (editMode) {
        updateMongoDBDataAdapterEntry(newEntryBuilder.build()).then(clearValuesAndReloadEntries).catch(_errorHandler("Cannot update entry", "Update failed"));
      } else {
        createMongoDBDataAdapterEntry(newEntryBuilder.build()).then(clearValuesAndReloadEntries).catch(_errorHandler("Cannot create entry", "Creation failed"));
      }
    },
    [clearValuesAndReloadEntries, dataAdapterId, editMode, storeId, storeKey, storeValues, ttl]
  );
  const resetForm = useCallback(() => {
    clearValues();
  }, [clearValues]);
  const editEntry = useCallback(
    (entry) => () => {
      setStoreId(entry.id);
      setStoreKey(entry.key);
      setStoreValues(entry.values);
      setTtl({ value: entry.ttl, unit: entry.ttlUnit, enabled: entry.ttl !== null });
      setEditMode(true);
    },
    []
  );
  const removeEntry = useCallback(
    (entry) => () => {
      deleteMongoDBDataAdapterEntry(entry).then(reloadEntries).catch(_errorHandler("Cannot delete entry", "Deletion failed"));
    },
    [reloadEntries]
  );
  const updateTTL = useCallback((value, unit, enabled) => {
    if (enabled && value) {
      setTtl({ value, unit, enabled });
    } else {
      setTtl({ value: null, unit: null, enabled: false });
    }
  }, []);
  const entryList = entries.map((entry) => {
    const key = entry.id;
    const values = entry.values || [];
    const expires = entry.expire_after || null;
    const valueList = slice(values, 0, 5).map((value) => /* @__PURE__ */ React.createElement("li", { key: `value-list-item-${value}` }, '"', value, '"'));
    if (values.length > 5) {
      valueList.push(
        /* @__PURE__ */ React.createElement("li", { key: "truncated" }, "[...] ", /* @__PURE__ */ React.createElement("em", null, "(edit entry to see all values)"))
      );
    }
    return /* @__PURE__ */ React.createElement("tr", { key }, /* @__PURE__ */ React.createElement("td", null, entry.key), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement("ol", { style: { paddingInlineStart: "1.5em" } }, valueList)), /* @__PURE__ */ React.createElement("td", null, expires ? /* @__PURE__ */ React.createElement(Timestamp, { dateTime: expires }) : "Does not expire"), /* @__PURE__ */ React.createElement("td", { style: { width: "8.5em" } }, /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { id: `edit-entry-${key}`, onClick: editEntry(entry), bsSize: "xsmall", bsStyle: "info" }, "Edit"), /* @__PURE__ */ React.createElement(Button, { id: `remove-entry-${key}`, onClick: removeEntry(entry), bsSize: "xsmall", bsStyle: "danger" }, "Delete"))));
  });
  if (dataAdapterId === "" || !dataAdapterId) {
    return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h3", null, "Entries can be added once the data adapter has been created."));
  }
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(
    LicenseCheck,
    {
      displayWarningContainer: true,
      bsStyle: "danger",
      title: "Modification of entries disabled",
      text: "The modification of entries in the database is disabled because there is no valid license installed."
    }
  ), /* @__PURE__ */ React.createElement(Row, { style: { marginBottom: 20 } }, /* @__PURE__ */ React.createElement("h3", { style: { marginBottom: 10 } }, "Create/update entries"), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      id: "lookup-key",
      label: "Key",
      value: storeKey,
      placeholder: "Enter key name here...",
      help: "The lookup key name",
      labelClassName: "col-sm-1",
      wrapperClassName: "col-sm-11",
      onChange: (event) => setStoreKey(trim(event.target.value))
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "textarea",
      id: "lookup-key-values",
      rows: 5,
      label: "Values",
      value: (storeValues || []).join("\n"),
      placeholder: "e.g. 127.0.0.1",
      help: "A list of values, one value per line.",
      labelClassName: "col-sm-1",
      wrapperClassName: "col-sm-11",
      onChange: (event) => setStoreValues(split(event.target.value, /\r?\n/))
    }
  ), /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: "TTL",
      update: updateTTL,
      value: ttl.value,
      help: "Custom TTL for entries",
      unit: ttl.unit || "MINUTES",
      units: ["MILLISECONDS", "SECONDS", "MINUTES", "HOURS", "DAYS"],
      enabled: ttl.enabled,
      labelClassName: "col-sm-1",
      wrapperClassName: "col-sm-11"
    }
  ), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { smOffset: 1, sm: 11 }, /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(
    Button,
    {
      id: "submit-entry",
      disabled: isEmpty(trimValues(storeValues)) || storeKey === "",
      type: "button",
      onClick: addEntry,
      bsSize: "small",
      bsStyle: "primary"
    },
    editMode ? "Update entry" : "Add new entry"
  ), /* @__PURE__ */ React.createElement(Button, { id: "clear-entry", onClick: resetForm, bsSize: "small" }, "Clear form"))))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement("h3", null, "Configured keys and values"), /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      onChange: handlePageChange,
      activePage: pagination == null ? void 0 : pagination.page,
      totalItems: pagination == null ? void 0 : pagination.total,
      pageSize: pagination == null ? void 0 : pagination.perPage,
      pageSizes: [10, 50, 100],
      useQueryParameter: false
    },
    /* @__PURE__ */ React.createElement(SearchForm, { onSearch: handleSearch, onReset: handleSearchReset, topMargin: 10 }),
    /* @__PURE__ */ React.createElement(Table, { condensed: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, "Key"), /* @__PURE__ */ React.createElement("th", null, "Values"), /* @__PURE__ */ React.createElement("th", null, "Expires At"), /* @__PURE__ */ React.createElement("th", null, "Action"))), /* @__PURE__ */ React.createElement("tbody", null, entryList))
  )));
};
export default MongoDBDataAdapterTable;
